//Dependencies
import React, { useState, useEffect, useRef, FormEvent } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { SHA256, enc } from 'crypto-js';
//Types
import { Admin, CookieTypes, UserRole } from '../Models';
import { FindCookieValue, GetBaseURL } from '../Utils';
import { setDashboardToken } from '../Utils/api';
import ErrorHandler from '../Components/Partials/ErrorHandler';
import { useUserData } from '../Components/Hooks/useUserData';
import Logo from '../assets/nft-sm-logo.png';

type LoginFlowProps = {
  routerProps: RouteComponentProps;
};

const LoginFLow: React.FC<LoginFlowProps> = ({ ...routerProps }) => {
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const EmailInput = useRef<HTMLInputElement>(null);
  const PasswordInput = useRef<HTMLInputElement>(null);
  const LoginForm = useRef<HTMLFormElement>(null);
  const { setUserAfterLogin, setUser } = useUserData();

  useEffect(() => {
    //if we have an encryopted cookie under "role", which can be decripted to match Admin, we set logged in to be true
    //So that we can redirect to the Admin Page.
    if (
      FindCookieValue(CookieTypes.FullName) &&
      FindCookieValue(CookieTypes.HomeRegion) &&
      FindCookieValue(CookieTypes.Email) &&
      FindCookieValue(CookieTypes.Role) &&
      FindCookieValue(CookieTypes.Role) === UserRole.Admin
    ) {
      let user: Admin = {
        email: FindCookieValue(CookieTypes.Email)! as string,
        role: FindCookieValue(CookieTypes.Role)! as string,
        full_name: FindCookieValue(CookieTypes.FullName)! as string,
        home_region: FindCookieValue(CookieTypes.HomeRegion)! as string,
      };
      setUserAfterLogin(user);
    }
  }, []);

  const resetError = () => {
    setError(null);
  };

  //!Handling
  const loginHandler = async (event: FormEvent) => {
    event.preventDefault();

    const Email = EmailInput.current!.value;
    const Password = PasswordInput.current!.value;
    const HashedWithSHA256 = SHA256(Password); //All passwords are stored using this hash --> should make a util function.
    const HashedPassword = HashedWithSHA256.toString(enc.Base64);

    const User = {
      email: Email,
      password: HashedPassword,
    };

    //!Route to login
    setSubmitting(true);
    let Request: Response;
    try {
      Request = await fetch(`${GetBaseURL()}/api/login/admin`, {
      method: 'POST',
      body: JSON.stringify(User),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    } catch (err) {
      setSubmitting(false);
      setError('Could not reach the server. Check your connection and try again.');
      return;
    }
    const Res = await Request.json().catch(() => ({ message: 'Unexpected server response.' }));
    setSubmitting(false);
    const ResponseStatus = Request.status;
    if (ResponseStatus === 200) {
      setDashboardToken(Res.dashboardToken || null);
      setUser(Res);
      setUserAfterLogin(Res);
    } else {
      setError(Res.message);
    }
  };

  return (
    <div className="login-page">
      <section className="login-page__art" aria-hidden="true">
        <div className="login-page__brand">
          <img src={Logo} alt="" />
          No Fuss Tutors
        </div>
        <div>
          <h2>Everything you need to run No Fuss Tutors.</h2>
          <p>Leads, matches, worksheet and reading users, and content uploads in one place.</p>
        </div>
        <span style={{ fontSize: '1.3rem', opacity: 0.7 }}>Admin dashboard</span>
      </section>
      <section className="login-page__form">
        <form className="login-card" onSubmit={loginHandler} ref={LoginForm}>
          <h1>Welcome back</h1>
          <p>Sign in with your No Fuss Tutors admin account.</p>
          {error ? <ErrorHandler error={error} type="error" handler={resetError} /> : null}
          <div className="field">
            <label htmlFor="login-email">Email</label>
            <input id="login-email" type="email" autoComplete="username" ref={EmailInput} placeholder="you@nofusstutors.com" required />
          </div>
          <div className="field">
            <label htmlFor="login-password">Password</label>
            <input id="login-password" type="password" autoComplete="current-password" ref={PasswordInput} placeholder="Password" required />
          </div>
          <button type="submit" className="btn--coral" disabled={submitting}>
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </section>
    </div>
  );
};

export default LoginFLow;
